import React, { FC, useCallback, useState, useReducer, useEffect } from 'react';
import { useInvokeSWRService } from '@dxb-web-ui-framework/common/hooks/DataService';
import { isEmpty } from '@dxb-web-ui-framework/common/utils/Helper';
import { action } from 'typesafe-actions';
import { useParams } from 'react-router-dom';
import { CheckMandateResponseBody } from '../../../../authorizations/src/domain/Authorization/entities/CheckMandateResponseDTO';
import { transactionService, authorizationService } from '../../../../../channels/secure-web/services';
import {
  useAccountDetailsExtraitTransactions,
  useAccountDetailsSelectedAccount,
} from '../../../../account/src/contexts/AccountDetails/hooks';
import { AccountExtraitTransactionDetailsProviderProps, AccountExtraitTransactionDetailsActionTypes } from './types';
import { AccountExtraitTransactionDetailsContext } from './AccountExtraitTransactionDetailsContext';
import { defaultAccountExtraitTransactionDetailsProviderState } from './defaultState';
import { AccountExtraitTransactionDetailsReducer } from './AccountExtraitTransactionDetailsReducer';

const AccountExtraitTransactionDetailsProvider: FC<AccountExtraitTransactionDetailsProviderProps> = ({
  children,
  transactionListData,
}) => {
  // services states
  const [timestamp] = useState(new Date().toISOString());
  const [state, dispatch] = useReducer(
    AccountExtraitTransactionDetailsReducer,
    defaultAccountExtraitTransactionDetailsProviderState,
  );

  const accountNumber = useAccountDetailsSelectedAccount()?.accountNumber;
  const { transactionNo: transactionNoParam } = useParams();

  // Account extrait Transaction details api call definition
  const shouldFetchAccountExtraitTransactionDetails =
    !isEmpty(transactionListData) &&
    !isEmpty(state.selectedAccountExtraitTransaction) &&
    Boolean(state.selectedAccountExtraitTransaction?.transactionId) &&
    Boolean(accountNumber);

  const {
    data: accountExtraitTransactionDetailsData,
    error: accountExtraitTransactionDetailsError,
    isLoading: isLoadingAccountExtraitTransactionDetails,
    mutate: accountExtraitDetailsMutation,
  } = useInvokeSWRService(
    transactionService.GetConsolidatedTransactionDetails,
    {
      orderId: state.selectedAccountExtraitTransaction?.transactionId,
      accountNumber,
      meta: {
        timestamp: state.selectedAccountExtraitTransaction?.timestamp,
      },
    },
    shouldFetchAccountExtraitTransactionDetails,
  );

  const shouldCheckAuthorization =
    Boolean(state.selectedAccountExtraitTransaction) &&
    state.selectedAccountExtraitTransaction !== null &&
    Boolean(accountExtraitTransactionDetailsData);

  // Check Authorization
  const {
    data: checkAuthorizationData,
    isLoading: isLoadingCheckAuthorization,
    error: checkAuthorizationError,
    mutate: checkAuthorizationMutation,
  } = useInvokeSWRService<CheckMandateResponseBody>(
    authorizationService.CheckMandate,
    {
      accountNumber: accountExtraitTransactionDetailsData?.fromAccount,
      uniqueId: Number(accountExtraitTransactionDetailsData?.transactionId),
      amount: Number(accountExtraitTransactionDetailsData?.amount),
      meta: {
        timestamp: state.selectedAccountExtraitTransaction?.timestamp ?? timestamp,
      },
    },
    shouldCheckAuthorization,
  );

  const onSelectAccountExtraitTransaction = useCallback(
    (transactionId?: string) => {
      if (transactionId && transactionListData) {
        const accountExtraitSelectedTransaction = transactionListData.find(
          transaction => transaction.transactionId === transactionId,
        );
        dispatch(
          action(
            AccountExtraitTransactionDetailsActionTypes.setSelectedAccountExtraitTransaction,
            { ...accountExtraitSelectedTransaction, timestamp: new Date().toISOString() } || null,
          ),
        );
      } else {
        dispatch(action(AccountExtraitTransactionDetailsActionTypes.setSelectedAccountExtraitTransaction, null));
      }
    },
    [transactionListData],
  );

  useEffect(() => {
    if (transactionNoParam && !state.selectedAccountExtraitTransaction) {
      onSelectAccountExtraitTransaction(transactionNoParam);
    }
  }, [transactionNoParam, onSelectAccountExtraitTransaction, state.selectedAccountExtraitTransaction]);

  const resetSelectedAccountExtraitTransaction = useCallback(() => {
    dispatch(action(AccountExtraitTransactionDetailsActionTypes.setSelectedAccountExtraitTransaction, null));
  }, []);

  const onAccountExtraitDetailsMutation = useCallback(() => {
    if (accountExtraitDetailsMutation) {
      accountExtraitDetailsMutation();
    }
  }, [accountExtraitDetailsMutation]);

  const onCheckAuthorizationMutation = useCallback(() => {
    checkAuthorizationMutation && checkAuthorizationMutation();
  }, [checkAuthorizationMutation]);

  const values = {
    ...state,
    checkAuthorizationData,
    accountExtraitTransactionDetailsData,
    accountExtraitTransactionDetailsError,
    isLoadingAccountExtraitTransactionDetails,
    isLoadingCheckAuthorization,
    checkAuthorizationError,
    onSelectAccountExtraitTransaction,
    resetSelectedAccountExtraitTransaction,
    onAccountExtraitDetailsMutation,
    onCheckAuthorizationMutation,
  };

  return (
    <AccountExtraitTransactionDetailsContext.Provider value={values}>
      {children}
    </AccountExtraitTransactionDetailsContext.Provider>
  );
};

const AccountExtraitTransactionDetailsProviderWrapper: FC<{}> = ({ children }) => {
  const transactionListData = useAccountDetailsExtraitTransactions();
  return (
    <AccountExtraitTransactionDetailsProvider transactionListData={transactionListData}>
      {children}
    </AccountExtraitTransactionDetailsProvider>
  );
};

export { AccountExtraitTransactionDetailsProvider, AccountExtraitTransactionDetailsProviderWrapper };
